"use client";

import { useEffect, useRef, useState, useMemo } from "react";
import dynamic from "next/dynamic";
import type { NearbyEvent } from "@/types/nearby";
import MapControls from "./MapControls";
import { haversineDistance } from "@/lib/geo";

const NearbyMapClient = dynamic(() => import("@/components/maps/NearbyMapClient"), {
  ssr: false,
  loading: () => <div className="map-skeleton" aria-hidden="true" />,
});

type Props = {
  pins: NearbyEvent[];
  user: { lat: number; lng: number };
  radiusMeters: number;
  height?: number | string;
  showControls?: boolean;
  showDateRange?: boolean;
  startDays?: number;
  endDays?: number;
  maxDays?: number; // Massimo numero di giorni per lo slider delle date (default: 30)
  onStartDaysChange?: (days: number) => void;
  onEndDaysChange?: (days: number) => void;
  onRadiusChange?: (km: number) => void;
  userLabel?: string; // Etichetta per il marker utente/centro
  /**
   * Funzione personalizzata per filtrare i pins. Se non fornita, usa il filtro di default
   * che filtra per raggio e date basandosi sulla posizione user.
   */
  filteredPins?: (
    pins: NearbyEvent[],
    radiusKm: number,
    startDays?: number,
    endDays?: number
  ) => NearbyEvent[];
  /**
   * Disabilita il filtro automatico per raggio e date. Utile se i pins sono già filtrati
   * e vuoi solo mostrare i controlli senza filtrare.
   */
  disableAutoFilter?: boolean;
};

/**
 * Funzione di default per filtrare i pins per raggio e date
 */
function defaultFilterPins(
  pins: NearbyEvent[],
  user: { lat: number; lng: number },
  radiusKm: number,
  startDays?: number,
  endDays?: number,
  maxDays?: number
): NearbyEvent[] {
  const nowTS = Date.now();
  const max = maxDays ?? 30;
  const startTS = nowTS + Math.max(0, Math.min(max, startDays ?? 0)) * 86400000;
  const endTS =
    nowTS + Math.max(0, Math.min(max, Math.max(startDays ?? 0, endDays ?? max))) * 86400000;

  return pins.filter((pin) => {
    // Filtro per date
    if (pin.date_from || pin.date_to) {
      const pinStartTS = pin.date_from ? new Date(pin.date_from).getTime() : 0;
      const pinEndTS = pin.date_to ? new Date(pin.date_to).getTime() : pinStartTS;
      if (!(pinEndTS >= startTS && pinStartTS <= endTS)) return false;
    }

    // Filtro per raggio
    const distanceKm = haversineDistance(
      { lat: user.lat, lon: user.lng },
      { lat: pin.lat, lon: pin.lng }
    );
    return distanceKm <= radiusKm;
  });
}

export default function LazyMap({
  pins: initialPins,
  user,
  radiusMeters: initialRadiusMeters,
  height = 320,
  showControls = true,
  showDateRange = false,
  startDays,
  endDays,
  maxDays = 30,
  onStartDaysChange,
  onEndDaysChange,
  onRadiusChange,
  userLabel,
  filteredPins,
  disableAutoFilter = false,
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [radiusKm, setRadiusKm] = useState(Math.round(initialRadiusMeters / 1000));
  // Gestione interna dello stato delle date se showDateRange è true e non ci sono handler esterni
  // Inizializza con le props se fornite, altrimenti usa i default
  const [internalStartDays, setInternalStartDays] = useState(startDays ?? 0);
  const [internalEndDays, setInternalEndDays] = useState(endDays ?? 30);
  // Flag per tracciare se dobbiamo usare lo stato interno o le props
  const useInternalState = showDateRange && !onStartDaysChange && !onEndDaysChange;

  // Quando ci sono handler esterni, le props sono la fonte di verità
  // Non sincronizziamo lo stato interno perché causerebbe loop
  // Usiamo direttamente le props quando disponibili, altrimenti lo stato interno (solo per inizializzazione)
  const currentStartDays = useInternalState ? internalStartDays : (startDays !== undefined ? startDays : internalStartDays);
  const currentEndDays = useInternalState ? internalEndDays : (endDays !== undefined ? endDays : internalEndDays);

  // Quando cambia il raggio, aggiorna anche la prop radiusMeters se c'è un handler
  useEffect(() => {
    if (onRadiusChange) {
      onRadiusChange(radiusKm);
    }
  }, [radiusKm, onRadiusChange]);

  // Sincronizza lo stato interno con le props iniziali SOLO al primo mount
  // (non quando cambiano dopo, per permettere all'utente di modificare i valori)
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (!hasInitializedRef.current && useInternalState) {
      // Inizializza solo una volta con i valori delle props
      if (startDays !== undefined) {
        setInternalStartDays(startDays);
      }
      if (endDays !== undefined) {
        setInternalEndDays(endDays);
      }
      hasInitializedRef.current = true;
    }
    // Nota: non includiamo startDays/endDays nelle dipendenze per evitare reset continui
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useInternalState]);

  // Nota: quando usiamo stato interno (useInternalState = true), non ci sono handler esterni,
  // quindi non dobbiamo notificare i callback. Gli handler esterni sono gestiti direttamente
  // dai controlli quando non usiamo stato interno.

  // Calcola i pins filtrati in base al raggio e alle date
  const pins = useMemo(() => {
    // Prima filtra sempre i pin non validi (coordinate non finite)
    const safe = initialPins.filter(
      (p) => Number.isFinite(p.lat) && Number.isFinite(p.lng)
    );

    // Se auto-filtro è disabilitato, restituisci i pins filtrati per validità
    if (disableAutoFilter) {
      return safe;
    }

    // Se è fornita una funzione personalizzata, usala (ma non può essere passata da Server Components)
    // Questa funzionalità è mantenuta per compatibilità con Client Components che passano funzioni
    if (filteredPins) {
      return filteredPins(safe, radiusKm, currentStartDays, currentEndDays);
    }

    // Usa il filtro di default con supporto per maxDays
    return defaultFilterPins(
      safe,
      user,
      radiusKm,
      currentStartDays,
      currentEndDays,
      maxDays
    );
  }, [
    initialPins,
    radiusKm,
    currentStartDays,
    currentEndDays,
    filteredPins,
    disableAutoFilter,
    user,
    maxDays,
  ]);

  // Calcola radiusMeters attuale
  const radiusMeters = radiusKm * 1000;

  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  const handleRadiusChange = (km: number) => {
    setRadiusKm(km);
  };

  const handleStartDaysChange = (days: number) => {
    // Se usiamo lo stato interno (showDateRange true e nessun handler esterno), aggiorna sempre lo stato interno
    if (useInternalState) {
      const v = Math.min(days, internalEndDays);
      setInternalStartDays(v);
    } else if (onStartDaysChange) {
      // Quando ci sono handler esterni, chiama direttamente l'handler esterno
      // Le props saranno aggiornate dal parent e verranno usate automaticamente
      const v = Math.min(days, currentEndDays);
      onStartDaysChange(v);
    }
  };

  const handleEndDaysChange = (days: number) => {
    // Se usiamo lo stato interno (showDateRange true e nessun handler esterno), aggiorna sempre lo stato interno
    if (useInternalState) {
      const v = Math.max(days, internalStartDays);
      setInternalEndDays(v);
    } else if (onEndDaysChange) {
      // Quando ci sono handler esterni, chiama direttamente l'handler esterno
      // Le props saranno aggiornate dal parent e verranno usate automaticamente
      const v = Math.max(days, currentStartDays);
      onEndDaysChange(v);
    }
  };

  return (
    <div className="map-section">
      <div ref={ref} style={{ minHeight: typeof height === "number" ? `${height}px` : height }}>
        {visible ? (
          <>
            {showControls && (
              <MapControls
                radiusKm={radiusKm}
                onRadiusChange={handleRadiusChange}
                startDays={currentStartDays}
                endDays={currentEndDays}
                onStartDaysChange={handleStartDaysChange}
                onEndDaysChange={handleEndDaysChange}
                showDateRange={showDateRange}
                maxDays={maxDays}
              />
            )}
            <NearbyMapClient items={pins} user={user} radiusMeters={radiusMeters} userLabel={userLabel} />
          </>
        ) : (
          <div className="map-skeleton" aria-hidden="true" />
        )}
        <noscript>
          <div style={{ padding: "8px 0" }}>
            <a href="#mappa" aria-label="Apri la mappa su Google Maps">
              Apri mappa
            </a>
          </div>
        </noscript>
      </div>
    </div>
  );
}
